/**
 * CKEditor 5 config and editor class for the template editor.
 * Aligned with CKEditor 5 sample (standard features only; merge variables via MergeTokenPlugin).
 */

import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Subscript,
  Superscript,
  List,
  ListProperties,
  TodoList,
  Link,
  Table,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TableToolbar,
  Undo,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageInsert,
  ImageInsertViaUrl,
  ImageUtils,
  Heading,
  Alignment,
  Font,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  BlockQuote,
  CodeBlock,
  Highlight,
  HorizontalLine,
  RemoveFormat,
  Fullscreen,
  ShowBlocks,
  FindAndReplace,
  TextPartLanguage,
  PageBreak,
  SpecialCharacters,
  Indent,
  IndentBlock,
  MediaEmbed,
  GeneralHtmlSupport,
  WordCount,
} from 'ckeditor5';
import MergeTokenPlugin from './MergeTokenPlugin.js';

/**
 * Editor class with merge token support. Use this with <CKEditor editor={TemplateClassicEditor} ... />.
 * Image upload is wired from the layout (hidden file input + uploadTemplateImage).
 */
class TemplateClassicEditor extends ClassicEditor {}

TemplateClassicEditor.builtinPlugins = [
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Subscript,
  Superscript,
  List,
  ListProperties,
  TodoList,
  Link,
  Table,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
  TableToolbar,
  Undo,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageInsert,
  ImageInsertViaUrl,
  ImageUtils,
  Heading,
  Alignment,
  Font,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  BlockQuote,
  CodeBlock,
  Highlight,
  HorizontalLine,
  RemoveFormat,
  Fullscreen,
  ShowBlocks,
  FindAndReplace,
  TextPartLanguage,
  PageBreak,
  SpecialCharacters,
  Indent,
  IndentBlock,
  MediaEmbed,
  GeneralHtmlSupport,
  WordCount,
  MergeTokenPlugin,
];

// Note: Merge variables are inserted via VariablePanel + ref.insertVariable() using our MergeTokenPlugin (insertMergeToken), not the premium MergeFields plugin.

// CKEditor premium/trial license (from CKEditor 5 sample). Must be passed in config when using React integration.
export const CKEDITOR_LICENSE_KEY =
  'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NzI4NDE1OTksImp0aSI6Ijg4MjM4YmY5LWZmN2UtNDljYS04YjQ3LTg0NDU5YTcyYTY0MyIsInVzYWdlRW5kcG9pbnQiOiJodHRwczovL3Byb3h5LWV2ZW50LmNrZWRpdG9yLmNvbSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsiY2xvdWQiLCJkcnVwYWwiLCJzaCJdLCJ3aGl0ZUxhYmVsIjp0cnVlLCJsaWNlbnNlVHlwZSI6InRyaWFsIiwiZmVhdHVyZXMiOlsiKiJdLCJ2YyI6IjRlNTVmYjk3In0.m5xosFnauiMY7Btxl-JRbKdrR9HLiWjGR8KA2e_zqW7wa70cfC6W3zyDqtVBH9KstKimqumci9-zAHbooAQPig';

TemplateClassicEditor.defaultConfig = {
  licenseKey: CKEDITOR_LICENSE_KEY,
  toolbar: {
    items: [
      'undo',
      'redo',
      '|',
      'showBlocks',
      'findAndReplace',
      'textPartLanguage',
      'fullscreen',
      '|',
      'heading',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'subscript',
      'superscript',
      'code',
      'removeFormat',
      '|',
      'specialCharacters',
      'horizontalLine',
      'pageBreak',
      'link',
      'insertImage',
      'insertImageViaUrl',
      'mediaEmbed',
      'insertTable',
      'highlight',
      'blockQuote',
      'codeBlock',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      'todoList',
      'outdent',
      'indent',
    ],
    shouldNotGroupWhenFull: false,
  },
  heading: {
    options: [
      { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
      { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
      { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
      { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
      { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
      { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
    ],
  },
  fontFamily: {
    supportAllValues: true,
  },
  fontSize: {
    options: [10, 12, 14, 'default', 18, 20, 22],
    supportAllValues: true,
  },
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: 'https://',
    decorators: {
      toggleDownloadable: {
        mode: 'manual',
        label: 'Downloadable',
        attributes: { download: 'file' },
      },
    },
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'],
    tableProperties: {
      defaultProperties: {
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#ccc',
      },
      borderColors: [
        { color: 'hsl(0, 0%, 0%)', label: 'Black' },
        { color: 'hsl(0, 0%, 40%)', label: 'Dark gray' },
        { color: 'hsl(0, 0%, 60%)', label: 'Gray' },
        { color: 'hsl(0, 0%, 80%)', label: 'Light gray' },
        { color: 'hsl(0, 0%, 100%)', label: 'White' },
      ],
    },
    tableCellProperties: {
      defaultProperties: {
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#ccc',
      },
      borderColors: [
        { color: 'hsl(0, 0%, 0%)', label: 'Black' },
        { color: 'hsl(0, 0%, 40%)', label: 'Dark gray' },
        { color: 'hsl(0, 0%, 60%)', label: 'Gray' },
        { color: 'hsl(0, 0%, 80%)', label: 'Light gray' },
        { color: 'hsl(0, 0%, 100%)', label: 'White' },
      ],
      backgroundColors: [
        { color: 'hsl(0, 0%, 100%)', label: 'White', hasBorder: true },
        { color: 'hsl(0, 0%, 97%)', label: 'Light gray' },
        { color: 'hsl(0, 0%, 93%)', label: 'Gray' },
        { color: 'hsl(45, 100%, 93%)', label: 'Light yellow' },
        { color: 'hsl(48, 100%, 88%)', label: 'Yellow' },
        { color: 'hsl(39, 100%, 50%)', label: 'Orange' },
        { color: 'hsl(4, 90%, 95%)', label: 'Light red' },
        { color: 'hsl(0, 75%, 92%)', label: 'Red' },
        { color: 'hsl(120, 40%, 94%)', label: 'Light green' },
        { color: 'hsl(120, 60%, 88%)', label: 'Green' },
        { color: 'hsl(200, 60%, 94%)', label: 'Light blue' },
        { color: 'hsl(210, 80%, 88%)', label: 'Blue' },
        { color: 'hsl(260, 50%, 94%)', label: 'Light purple' },
        { color: 'hsl(280, 60%, 90%)', label: 'Purple' },
      ],
    },
  },
  image: {
    toolbar: [
      'toggleImageCaption',
      'imageTextAlternative',
      '|',
      'imageStyle:inline',
      'imageStyle:wrapText',
      'imageStyle:breakText',
      '|',
      'resizeImage',
    ],
  },
  htmlSupport: {
    allow: [
      {
        name: /^.*$/,
        styles: true,
        attributes: true,
        classes: true,
      },
    ],
  },
  placeholder: 'Start typing or insert merge variables...',
};

export { TemplateClassicEditor };
export default TemplateClassicEditor;
